// Cross-tab communication for impersonation state using BroadcastChannel API.
// When impersonation starts in one tab, other tabs are notified so they can
// clear their stale state and avoid showing incorrect context.
//
// Includes localStorage fallback for browsers without BroadcastChannel (Safari < 15.4)

const CHANNEL_NAME = 'm8_impersonation';
const STORAGE_KEY = 'm8_impersonation_broadcast';

let channel = null;
let listeners = [];
let usingLocalStorageFallback = false;

/**
 * Handle storage events for localStorage fallback
 */
function handleStorageEvent(event) {
  if (event.key !== STORAGE_KEY || !event.newValue) return;

  try {
    const { type, data } = JSON.parse(event.newValue);
    listeners.forEach((listener) => {
      try {
        listener(type, data);
      } catch (e) {
        console.error('[ImpersonationChannel] Listener error:', e);
      }
    });
  } catch (e) {
    // Ignore parse errors
  }
}

/**
 * Initialize the broadcast channel if supported.
 * Call this early in app initialization.
 */
export function initImpersonationChannel() {
  if (channel || usingLocalStorageFallback) return; // Already initialized

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event) => {
        const { type, data } = event.data || {};
        listeners.forEach((listener) => {
          try {
            listener(type, data);
          } catch (e) {
            // Don't let listener errors break the channel
            console.error('[ImpersonationChannel] Listener error:', e);
          }
        });
      };
      return;
    } catch (e) {
      // BroadcastChannel creation failed, fall through to localStorage fallback
      console.warn('[ImpersonationChannel] BroadcastChannel failed, using localStorage fallback:', e);
    }
  }

  // localStorage fallback for browsers without BroadcastChannel (Safari < 15.4)
  try {
    usingLocalStorageFallback = true;
    window.addEventListener('storage', handleStorageEvent);
  } catch (e) {
    console.warn('[ImpersonationChannel] Failed to initialize localStorage fallback:', e);
    usingLocalStorageFallback = false;
  }
}

/**
 * Subscribe to impersonation state changes from other tabs.
 * @param {Function} callback - Called with (type, data) when a message is received
 * @returns {Function} Unsubscribe function
 */
export function onImpersonationChange(callback) {
  if (typeof callback !== 'function') return () => {};

  listeners.push(callback);

  return () => {
    listeners = listeners.filter((l) => l !== callback);
  };
}

/**
 * Internal function to broadcast a message via the appropriate channel
 */
function broadcastMessage(type, data) {
  const message = {
    type,
    data: {
      ...data,
      timestamp: Date.now(),
      tabId: getTabId(),
    },
  };

  if (channel) {
    try {
      channel.postMessage(message);
    } catch (e) {
      console.warn('[ImpersonationChannel] Failed to broadcast:', e);
    }
  } else if (usingLocalStorageFallback) {
    // localStorage fallback - write to localStorage to trigger storage event in other tabs
    try {
      // Write the message - storage event will fire in other tabs
      localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
      // Delay cleanup to ensure storage event fires in all tabs (Safari < 15.4 race condition fix)
      // The storage event only fires in other tabs, not the originating tab
      setTimeout(() => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (cleanupError) {
          // Ignore cleanup errors - the message was already broadcast
        }
      }, 100);
    } catch (e) {
      console.warn('[ImpersonationChannel] Failed to broadcast via localStorage:', e);
    }
  }
}

/**
 * Broadcast that impersonation has started in this tab.
 * Other tabs should clear their state and show a warning.
 * @param {Object} data - Optional data about the impersonation
 */
export function broadcastImpersonationStarted(data = {}) {
  broadcastMessage('IMPERSONATION_STARTED', data);
}

/**
 * Broadcast that impersonation has ended in this tab.
 */
export function broadcastImpersonationEnded() {
  broadcastMessage('IMPERSONATION_ENDED', {});
}

/**
 * Close the channel. Call on app unmount.
 */
export function closeImpersonationChannel() {
  if (channel) {
    try {
      channel.close();
    } catch (e) {
      // Ignore close errors
    }
    channel = null;
  }
  if (usingLocalStorageFallback) {
    try {
      window.removeEventListener('storage', handleStorageEvent);
    } catch (e) {
      // Ignore removal errors
    }
    usingLocalStorageFallback = false;
  }
  listeners = [];
}

// Generate a unique ID for this tab to avoid reacting to our own messages
let tabId = null;
function getTabId() {
  if (!tabId) {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
  return tabId;
}

/**
 * Check if a message originated from this tab.
 * @param {Object} data - The data from the message
 * @returns {boolean}
 */
export function isFromThisTab(data) {
  return data && data.tabId === getTabId();
}

/**
 * Check an axios response for impersonation timeout headers and display notification.
 * Call this from an axios response interceptor to handle session timeout messages.
 * @param {Object} response - Axios response object
 * @returns {Object} The response (unchanged)
 */
export function checkImpersonationTimeout(response) {
  if (!response || !response.headers) return response;

  const expired = response.headers['x-impersonation-expired'];
  const message = response.headers['x-impersonation-message'];

  if (expired === 'true' && message) {
    // Dynamically import to avoid circular dependencies
    import('antd').then(({ message: antdMessage }) => {
      antdMessage.warning(message, 5);
    }).catch(() => {
      // Fallback if antd import fails
      // eslint-disable-next-line no-alert
      alert(message);
    });
  }

  return response;
}

/**
 * Setup axios interceptor to handle impersonation timeout responses.
 * Call this once during app initialization.
 * @param {Object} axios - The axios instance to configure
 */
export function setupImpersonationTimeoutInterceptor(axios) {
  if (!axios || !axios.interceptors) return;

  axios.interceptors.response.use(
    (response) => checkImpersonationTimeout(response),
    (error) => {
      // Also check error responses (4xx, 5xx)
      if (error && error.response) {
        checkImpersonationTimeout(error.response);
      }
      return Promise.reject(error);
    },
  );
}
